import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Public as PublicIcon, Place as PlaceIcon, Groups as ReachIcon } from '@mui/icons-material';
import SponsorPublicShell from '@components/sponsor/SponsorPublicShell';
import ExpressInterestForm from '@components/sponsor/ExpressInterestForm';
import {
  getOpportunityTypeLabel,
  getTierLabel,
  tierGradient,
  type SponsorshipOpportunity,
} from '@utils/sponsor';

const SponsorOpportunitiesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sportFilter = searchParams.get('sport');
  const [opportunities, setOpportunities] = useState<SponsorshipOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SponsorshipOpportunity | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ status: 'open' });
        if (sportFilter) qs.set('sport', sportFilter);
        const res = await fetch(`/api/sponsorship-opportunities?${qs.toString()}`);
        const data = await res.json();
        setOpportunities(data.success ? data.data : []);
      } catch {
        setOpportunities([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [sportFilter]);

  const hero = (
    <Box sx={{ py: { xs: 4, md: 6 }, background: 'linear-gradient(180deg, #111827 0%, #0b1220 100%)' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', mb: 1 }}>
          Open sponsorship opportunities
        </Typography>
        <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: 720 }}>
          Browse slots across contests, training content, and new formats. Tier and package are customised to your budget — no public pricing listed.
          {sportFilter ? ` Showing opportunities for ${sportFilter}.` : ''}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <SponsorPublicShell hero={hero}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#818cf8' }} />
        </Box>
      ) : opportunities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 2 }}>No open opportunities right now</Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Submit a general interest form and we will reach out when matching slots open.
          </Typography>
          <ExpressInterestForm compact />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {opportunities.map((opp) => (
            <Grid item xs={12} md={6} key={opp.id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'rgba(129, 140, 248, 0.5)', transform: 'translateY(-2px)' },
                }}
              >
                <Box sx={{ height: 6, background: tierGradient(opp.sponsorshipTier) }} />
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip
                      size="small"
                      label={getTierLabel(opp.sponsorshipTier)}
                      sx={{ fontWeight: 900, bgcolor: 'rgba(251, 191, 36, 0.15)', color: '#fcd34d' }}
                    />
                    <Chip size="small" label={getOpportunityTypeLabel(opp.opportunityType)} sx={{ fontWeight: 700, color: '#c7d2fe' }} />
                    {opp.sport && <Chip size="small" icon={<PublicIcon />} label={opp.sport} sx={{ fontWeight: 700 }} />}
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc', mb: 1 }}>{opp.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, minHeight: 48 }}>{opp.description}</Typography>
                  <Stack spacing={0.75} sx={{ mb: 2 }}>
                    {opp.estimatedReach && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ReachIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{opp.estimatedReach}</Typography>
                      </Stack>
                    )}
                    {opp.geography && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PlaceIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{opp.geography}</Typography>
                      </Stack>
                    )}
                    {typeof opp.slotsAvailable === 'number' && (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {opp.slotsAvailable} slot{opp.slotsAvailable === 1 ? '' : 's'} available
                      </Typography>
                    )}
                  </Stack>
                  <Button
                    variant="contained"
                    onClick={() => setSelected(opp)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    }}
                  >
                    Express interest
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{selected?.title}</DialogTitle>
        <DialogContent>
          {selected && <ExpressInterestForm opportunity={selected} onSuccess={() => setSelected(null)} />}
        </DialogContent>
      </Dialog>
    </SponsorPublicShell>
  );
};

export default SponsorOpportunitiesPage;
